import {
  add,
  subtract,
  distance2BetweenPoints,
  angleBetweenVectors,
  multiplyScalar,
  normalize,
  multiply3x3_vect3,
  radiansFromDegrees,
  dot,
  cross,
} from '@kitware/vtk.js/Common/Core/Math';
import type { Vector2, Vector3, Matrix3x3 } from '@kitware/vtk.js/types';
import type { VtkDirection } from 'types/vtk';
import type { $AxisDirection, IComputeHistogram } from '@kitware/vtk.js';
import type { vtkImageData } from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';
import type { Segment } from '../types';
import { CORRECTED_IMAGE_TAGS, IMAGE_NORMALS, IMAGE_VIEW_UPS, PET_UNITS } from 'config/constants';
import type { DreAllViewportTypes } from 'config/constants';
import type { Region, FrameDataFragment } from 'generated/graphql';
import type { ImageRegistration, Stack } from '../../ViewportsConfigurations/types';
import { getLPSDirections } from '../modules/imaging/utils';
import type { BaseImagingProvider } from '../modules/imaging/BaseImagingProvider';
import type { IView } from 'react-vtk-js';
import type { vtkRenderer } from '@kitware/vtk.js/Rendering/Core/Renderer';
import { TypedArrayType } from 'utils/typedArray';

export type ScaleFactorTags = {
  readonly petImage: {
    readonly correctedImage: ReadonlyArray<string>;
    readonly decayCorrection: string;
    readonly units: string;
    readonly startDateTime: string;
    readonly scanDateTime: string;
    readonly frameReferenceTime?: Nullable<number>;
    readonly suvScaleFactor?: Nullable<number>;
    readonly preCalculatedSuvBwFactor?: Nullable<number>;
    readonly radiopharmaceuticalInformation: {
      readonly radionuclideTotalDose?: Nullable<number>;
      readonly radionuclideHalfLife?: Nullable<number>;
    };
  };
  // Want to extend the FrameDataFragment module but it is being a pain
  readonly patientStudy: {
    patientSex?: string;
    patientWeight: Nullable<number>;
    patientSize?: Nullable<number>;
    leanBodyMass?: Nullable<number>;
  };
};

export function areViewportsParallel(
  direction1: VtkDirection,
  direction2: VtkDirection,
  normal1: Vector3,
  normal2: Vector3
): {
  parallel: boolean;
  normal1: Vector3;
  normal2: Vector3;
  angle: number;
} {
  const direction13x3Matrix = make3x3MatrixFromDirection(direction1);

  multiply3x3_vect3(direction13x3Matrix, normal1, normal1);
  const direction23x3Matrix = make3x3MatrixFromDirection(direction2);

  multiply3x3_vect3(direction23x3Matrix, normal2, normal2);

  const angle = angleBetweenVectors(normal1, normal2);
  const angleIsCloseTo360 = Math.abs(angle - Math.PI) < 0.01;
  const angleIsCloseTo0 = angle < 0.01;
  return { parallel: angleIsCloseTo0 || angleIsCloseTo360, normal1, normal2, angle };
}
export function areNormalsParallel(normal1: Vector3, normal2: Vector3): boolean {
  // radians
  const angle = angleBetweenVectors(normal1, normal2);
  // we allow a tight tolerance of 0.01 radians
  // a value of 3.14... === 360 degrees
  const angleIsCloseTo360 = Math.abs(angle - Math.PI) < 0.01;
  const angleIsCloseTo0 = angle < 0.01;
  return angleIsCloseTo0 || angleIsCloseTo360;
}
export function areNormalsParallelWithTolerance(
  normal1: Vector3,
  normal2: Vector3,
  tolerance: number
): boolean {
  const angle = angleBetweenVectors(normal1, normal2);
  // we allow a specified tolerance in our check
  // a value of 3.14... === 360 degrees
  const angleIsCloseTo360 = Math.abs(angle - Math.PI) < tolerance;
  const angleIsCloseTo0 = angle < tolerance;
  return angleIsCloseTo0 || angleIsCloseTo360;
}
export function make3x3MatrixFromDirection(direction: VtkDirection): Matrix3x3 {
  // vtk.js is still row-major, but just in a single array
  return [
    direction[0],
    direction[3],
    direction[6],
    direction[1],
    direction[4],
    direction[7],
    direction[2],
    direction[5],
    direction[8],
  ];
}

export const calculateAxis = (direction: VtkDirection): $AxisDirection => {
  const direction3x3Matrix = make3x3MatrixFromDirection(direction);
  const normal1: Vector3 = [0, 0, -1];
  multiply3x3_vect3(direction3x3Matrix, normal1, normal1);
  const [dx, dy, dz] = normal1.map(Math.abs);
  if (dx > dz && dx > dy) {
    return 'X';
  }
  if (dy > dz && dy > dx) {
    return 'Y';
  }
  if (dx < dz && dz > dy) {
    return 'Z';
  }
  return 'X';
};

export const calculateMaxSlices = (
  image: Nullable<vtkImageData>,
  viewType: DreAllViewportTypes
): number => {
  const imageIndex = viewType === 'TWO_D_DRE' ? 2 : 1;
  return image ? image.getDimensions()[imageIndex] : 0;
};

const threshold = Math.sqrt(0.5);

function roundToUnitVector(
  inVec: [number, number, number],
  outVec: [number, number, number]
): void {
  for (let axis = 0; axis < inVec.length; ++axis) {
    const val = inVec[axis];
    outVec[axis] = val < -threshold ? -1 : val > threshold ? 1 : 0;
  }
}

const calculateBoundingSphereRadius = (bounds: number[]): number => {
  const dimensions = [
    bounds[1] - bounds[0], // x dimension
    bounds[3] - bounds[2], // y dimension
    bounds[5] - bounds[4], // z dimension
  ];

  // Half the diagonal of the bounding box
  const radius =
    0.5 *
    Math.sqrt(Math.pow(dimensions[0], 2) + Math.pow(dimensions[1], 2) + Math.pow(dimensions[2], 2));

  // Minimum radius of 1.0 to prevent division by zero issues
  if (radius === 0) {
    throw new Error('Invalid image bounds');
  }
  return radius;
};

// @ts-expect-error [EN-10263] - TS7006 - Parameter 'imageDimensions' implicitly has an 'any' type. | TS7006 - Parameter 'imageSpacing' implicitly has an 'any' type.
export const calculateImageWidthWorld = (imageDimensions, imageSpacing) => {
  return imageDimensions[0] * imageSpacing[0];
};

// @ts-expect-error [EN-10263] - TS7006 - Parameter 'viewNode' implicitly has an 'any' type. | TS7006 - Parameter 'renderer' implicitly has an 'any' type. | TS7006 - Parameter 'canvasWidth' implicitly has an 'any' type.
export const calculateViewportWidthWorld = (viewNode, renderer, canvasWidth) => {
  const p0 = viewNode.displayToWorld(0, 0, 0, renderer);
  const p1 = viewNode.displayToWorld(canvasWidth, 0, 0, renderer);

  return Math.sqrt(
    Math.pow(p1[0] - p0[0], 2) + Math.pow(p1[1] - p0[1], 2) + Math.pow(p1[2] - p0[2], 2)
  );
};

// @ts-expect-error [EN-10263] - TS7006 - Parameter 'isAnterior' implicitly has an 'any' type.
export const determineDisplacementFactor = (isAnterior) => {
  return isAnterior ? 1 : -1;
};

export const alignImageDataWithSideOfViewport = (
  imageCenter: Vector3,
  sideAxis: Vector3,
  sliceNormal: Vector3,
  imageWidthWorld: number,
  viewportWidthWorld: number,
  displacementFactor: number,
  boundingSphereRadius: number,
  imageDirection: VtkDirection
) => {
  const displacementDistance = displacementFactor * (viewportWidthWorld / 2 - imageWidthWorld / 2);

  const scaledDisplacement = [
    sideAxis[0] * displacementDistance,
    sideAxis[1] * displacementDistance,
    sideAxis[2] * displacementDistance,
  ];

  const newPosition = [
    imageCenter[0] + scaledDisplacement[0],
    imageCenter[1] + scaledDisplacement[1],
    imageCenter[2] + scaledDisplacement[2],
  ];

  const newFocalPoint = [...newPosition];

  const radius = boundingSphereRadius === 0 ? 1.0 : boundingSphereRadius;
  const distance = 2 * radius;

  const offset = [sliceNormal[0] * distance, sliceNormal[1] * distance, sliceNormal[2] * distance];

  const finalPosition = [
    newPosition[0] - offset[0],
    newPosition[1] - offset[1],
    newPosition[2] - offset[2],
  ];

  const viewUp = calculateViewUpFromDirection(imageDirection);

  return {
    position: finalPosition as Vector3,
    focalPoint: newFocalPoint as Vector3,
    viewUp,
  };
};

export const calculateChestWallAlignedCameraPositionFromImageData = (
  imagingProvider: BaseImagingProvider<Stack>,
  view: IView,
  renderer: vtkRenderer,
  viewType: DreAllViewportTypes,
  viewportId: string
): {
  viewUp: Vector3;
  position: Vector3;
  focalPoint: Vector3;
} => {
  // renderer should never be undefined, but just in case
  if (renderer == null || renderer.isDeleted()) {
    throw new Error('Renderer is not defined');
  }
  const image = imagingProvider?.vtkImage;
  const activeSlicePlane = imagingProvider.getActiveSlicePlane(viewType, viewportId);
  const activeSlice = imagingProvider.getActiveSlice(viewType, viewportId);
  const activeSliceTags = imagingProvider.getFrameTagsForViewIndex(viewType, activeSlice);
  const viewNode = view?.getOpenGLRenderWindow()?.get();
  const [canvasWidth] = viewNode?.getViewportSize(renderer) ?? [1, 1];

  if (!activeSlicePlane || !activeSliceTags) {
    throw new Error('Active slice plane, or active slice tags are not defined');
  }

  const imageCenter = image.getCenter();
  const sideAxis = activeSliceTags.direction.data.slice(0, 3) as Vector3;
  const sliceNormal = activeSlicePlane.sliceNormal;
  const imageWidthWorld = calculateImageWidthWorld(image.getDimensions(), image.getSpacing());

  const viewportWidthWorld = calculateViewportWidthWorld(viewNode, renderer, canvasWidth);

  const displacementFactor = determineDisplacementFactor(
    imagingProvider.activeSliceRowDirectionIs('anterior')
  );
  const boundingSphereRadius = calculateBoundingSphereRadius([...image.getBounds()]);
  const imageDirection = image.getDirection() as VtkDirection;

  return alignImageDataWithSideOfViewport(
    imageCenter,
    sideAxis,
    sliceNormal,
    imageWidthWorld,
    viewportWidthWorld,
    displacementFactor,
    boundingSphereRadius,
    imageDirection
  );
};

export const calculateCameraParameters = (
  imagingProvider: BaseImagingProvider<Stack>,
  view: IView,
  renderer: vtkRenderer,
  viewType: DreAllViewportTypes,
  viewportId: string,
  shouldAlignWithChestWall: boolean
): {
  viewUp: Vector3;
  position: Vector3;
  focalPoint: Vector3;
} => {
  if (shouldAlignWithChestWall && renderer != null) {
    return calculateChestWallAlignedCameraPositionFromImageData(
      imagingProvider,
      view,
      renderer,
      viewType,
      viewportId
    );
  } else {
    return calculateDefaultCameraParametersFromImageData(viewType, imagingProvider.vtkImage);
  }
};

export const calculateDefaultCameraParametersFromImageData = (
  viewType: DreAllViewportTypes,
  image: vtkImageData
): {
  viewUp: Vector3;
  position: Vector3;
  focalPoint: Vector3;
  normal: Vector3;
} => {
  const d9 = image.getDirection() as Matrix3x3;
  let viewUp: Vector3 = [...IMAGE_VIEW_UPS[viewType]];

  const d3x3 = make3x3MatrixFromDirection(d9);
  const d3x3_inv = d9;
  const normal: Vector3 = [...IMAGE_NORMALS[viewType]];
  let offset = normal;
  const lps = getLPSDirections(d9);

  if (viewType !== 'TWO_D_DRE') {
    multiply3x3_vect3(d3x3_inv as Matrix3x3, normal, normal);
    multiply3x3_vect3(d3x3_inv as Matrix3x3, viewUp, viewUp);
    roundToUnitVector(normal, normal);
    roundToUnitVector(viewUp, viewUp);
    if (viewType === 'THREE_D_MIP') {
      //positioning camera to show patient in an upright position for MIP volumes
      viewUp = [...lps.Superior] as Vector3;
      offset = [...lps.Anterior] as Vector3;
    }
  }

  multiply3x3_vect3(d3x3, normal, normal);
  multiply3x3_vect3(d3x3, viewUp, viewUp);

  /*
   * Camera positioning calculation
   *
   * We use parallel projection, so this is simple. We need to simply position the camera away from the image.
   *
   * The view frustrum is the 3D volume of space visible from the camera
   * It's a rectangle
   *
   * Here's how it works:
   *
   * 1. We have a sphere (bounding sphere) containing our 3D object
   * 2. We move the camera outside of the sphere (arbitrary -- it just needs to face the image)
   *
   */

  const focalPoint = image.getCenter();
  let radius = calculateBoundingSphereRadius([...image.getBounds()]);

  // If we have just a single point, pick a radius of 1.0
  radius = radius === 0 ? 1.0 : radius;
  const distance = 2 * radius;

  const position = focalPoint.map((e, i) => e - offset[i] * distance) as Vector3;

  return { viewUp, position, focalPoint, normal };
};

export const getMiddlePoint = (a: Vector3, b: Vector3): Vector3 => {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
};

// Function to convert radians to degrees
export function radiansToDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

// Given a triangle abc, find the point 'd' on segment 'ac', such that:
// vector 'bd' bisects the angle formed at vertex b.
export function calculateAngleBisectingPoint(a: Vector3, b: Vector3, c: Vector3): Vector3 {
  const len_ba = Math.sqrt(distance2BetweenPoints(b, a));
  const len_bc = Math.sqrt(distance2BetweenPoints(b, c));
  const len_ac = Math.sqrt(distance2BetweenPoints(a, c));
  const len_ad = (len_ac * len_ba) / (len_ba + len_bc);
  const vec_ac = getVectorsDifference(c, a);
  normalize(vec_ac);
  const vec_ad = multiplyScalar(vec_ac, len_ad);
  const out_d: Vector3 = [0, 0, 0];
  return add(vec_ad, a, out_d);
}

// Transform offset information (normalized direction in world coordinates and pixel length)
// into a vector in display coordinates.
export function worldToDisplayOffset(
  directionInWorld: Vector3 | number[],
  distanceInPixels: number,
  openglRenderWindow: vtkOpenGLRenderWindow,
  renderer: vtkRenderer
): Vector3 {
  const vecInPixels = getVectorsDifference(
    openglRenderWindow.worldToDisplay(
      directionInWorld[0],
      directionInWorld[1],
      directionInWorld[2],
      renderer
    ),
    openglRenderWindow.worldToDisplay(0, 0, 0, renderer)
  );
  normalize(vecInPixels);
  return multiplyScalar(vecInPixels, distanceInPixels);
}

// Function to find the angle in 3D space
export function findAngleBetweenThreePoints(a: Vector3, b: Vector3, c: Vector3): number {
  const ab = Math.sqrt(distance2BetweenPoints(a, b));
  const bc = Math.sqrt(distance2BetweenPoints(b, c));
  const ac = Math.sqrt(distance2BetweenPoints(a, c));

  const angle = (Math.pow(ab, 2) + Math.pow(bc, 2) - Math.pow(ac, 2)) / (2 * ab * bc);
  // acos will end up NaN when greater than 1 or less than -1
  return radiansToDegrees(Math.acos(Math.min(Math.max(angle, -1), 1)));
}

export function distanceBetweenPoints(a: Vector3, b: Vector3): number {
  return Math.sqrt(distance2BetweenPoints(a, b));
}

export function getVectorsDifference(origin: Vector3, destination: Vector3): Vector3 {
  return [origin[0] - destination[0], origin[1] - destination[1], origin[2] - destination[2]];
}

/*
 * Returns an angle difference between two vectors given vec1 as its origin
 * to know whether we are rotating clockwise or counter-clockwise
 *
 * Return value is in radians
 */
export function orientedAngleBetweenVectors(vec1: Vector3, vec2: Vector3, normal: Vector3): number {
  return Math.atan2(dot(cross(vec2, vec1, [0, 0, 0]), normal), dot(vec1, vec2));
}

export function getCobbAngleMeasurement(
  start1: Vector3,
  end1: Vector3,
  start2: Vector3,
  end2: Vector3
): number {
  const vec1 = getVectorsDifference(start1, end1);
  const vec2 = getVectorsDifference(start2, end2);

  const angle = radiansToDegrees(angleBetweenVectors(vec1, vec2));

  if (angle > 90) {
    return 180 - angle;
  } else {
    return angle;
  }
}

// We don't need all the powerful features provided by Ramda#equals
export const quickVec3Comparison = (a: Vector3, b: Vector3): boolean => {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
};

export const quickNullableVec3Comparison = (a?: Vector3 | null, b?: Vector3 | null): boolean => {
  if (a == null || b == null) {
    if (a == null && b == null) return true;
    return false;
  }

  return quickVec3Comparison(a, b);
};

export const quickSegmentComparison = (a: Segment, b: Segment): boolean => {
  return quickVec3Comparison(a[0], b[0]) && quickVec3Comparison(a[1], b[1]);
};

export const quickAllSegmentsComparison = (
  a: ReadonlyArray<Segment>,
  b: ReadonlyArray<Segment>
): boolean => {
  return a.length === b.length && a.every((sA, idx) => quickSegmentComparison(sA, b[idx]));
};

export const sortedAscending = (array: Array<number>): boolean => {
  return array.every(function (num, idx, arr) {
    return num <= arr[idx + 1] || idx === arr.length - 1 ? 1 : 0;
  });
};

export const sortedDescending = (array: Array<number>): boolean => {
  return array.every(function (num, idx, arr) {
    return num >= arr[idx + 1] || idx === arr.length - 1 ? 1 : 0;
  });
};

export const findSmallestTagSmid = (tags: FrameDataFragment[]): string => {
  let smallestIndex = 0;
  for (let i = 1; i < tags.length; i++) {
    if (tags[i].imageNumber < tags[smallestIndex].imageNumber) {
      smallestIndex = i;
    }
  }
  return tags[smallestIndex].smid;
};

export function getSliceParametersForImageByViewType({
  viewType,
  image,
}: {
  viewType: string;
  image: Nullable<vtkImageData>;
}): [number, number] {
  if (image == null) return [0, 0];
  const bounds = image?.getBounds();
  const extent = image?.getExtent();
  const roundBounds = (min: number, max: number) => [Math.round(min), Math.round(max)];

  if (!bounds || !extent) return [0, 0];
  switch (viewType) {
    case 'TWO_D_DRE':
      return [extent[4], extent[5]];
    case 'AXIAL_DRE':
      return roundBounds(bounds[4], bounds[5]) as Vector2;
    case 'CORONAL_DRE':
      return roundBounds(bounds[2], bounds[3]) as Vector2;
    case 'SAGITTAL_DRE':
      return roundBounds(bounds[0], bounds[1]) as Vector2;
    default:
      return [extent[4], extent[5]];
  }
}

// Move a given point towards the rendering camera by the provided offset distance.
export function offsetTowardsCamera(
  renderer: vtkRenderer,
  wcPoint: Vector3,
  offsetDistance: number = 1.1
): ReadonlyArray<number> {
  const vcPoint = renderer.worldToView(wcPoint[0], wcPoint[1], wcPoint[2]);
  return renderer.viewToWorld(vcPoint[0], vcPoint[1], vcPoint[2] + offsetDistance);
}

/**
 * Calculate the normalized distance between center of ellipse to the test point such that:
 *    normalizedDistance < 1 --> point inside ellipse,
 *    normalizedDistance == 1 --> point on ellipse,
 *    normalizedDistance > 1 --> point outside ellipse
 */
export function isInsideEllipse(
  center: Vector2,
  rx: number,
  ry: number,
  testPoint: Vector3
): boolean {
  const normalizedDistance =
    Math.pow(testPoint[0] - center[0], 2) / (rx * rx) +
    Math.pow(testPoint[1] - center[1], 2) / (ry * ry);
  return normalizedDistance < 1;
}

/** Stencil function to be used for region of interest calculations */
export type StencilFunction = (point: Vector3) => boolean;
/**
 * Creates a stencil function to filter voxels that lie inside the ellipse. Returns true if a
 * voxel's center is inside the ellipse, and false otherwise.
 */
export const getStencilFunction = (
  sliceNumber: number,
  [spacingX, spacingY, spacingZ]: Vector3,
  isInsideBounds: (gridPoint: Vector3) => boolean
): StencilFunction => {
  return (idx: Vector3): boolean => {
    if (sliceNumber !== idx[2]) return false;
    const gridPoint: Vector3 = [idx[0] * spacingX, idx[1] * spacingY, idx[2] * spacingZ];
    return isInsideBounds(gridPoint);
  };
};

/** Orientation-independent world-space description of an elliptical ROI on a slice plane
 *  (center, orthonormal in-plane axes, normal, semi-axes rx/ry in mm, on-slice halfThickness). */
export type WorldEllipseGeometry = {
  center: Vector3;
  inPlaneX: Vector3;
  inPlaneY: Vector3;
  normal: Vector3;
  rx: number;
  ry: number;
  halfThickness: number;
};

const toUnitVector = (v: Vector3): Vector3 => {
  const out: Vector3 = [v[0], v[1], v[2]];
  normalize(out);
  return out;
};

/** Builds {WorldEllipseGeometry} from the drawn world corners + view basis (normal/view-up).
 *  `indexToWorld` MUST be the transform of the same image passed to computeHistogram. */
export const buildWorldEllipseGeometry = ({
  center,
  cornerA,
  cornerB,
  viewUp,
  viewNormal,
  rotationDeg = 0,
  indexToWorld,
}: {
  center: Vector3;
  cornerA: Vector3;
  cornerB: Vector3;
  viewUp: Vector3;
  viewNormal: Vector3;
  rotationDeg?: number;
  indexToWorld: (idx: Vector3) => Vector3;
}): WorldEllipseGeometry => {
  const normal = toUnitVector(viewNormal);

  // Orthonormal in-plane basis from view-up + normal (rotated below for a rotated ROI).
  let inPlaneX: Vector3 = [0, 0, 0];
  cross(toUnitVector(viewUp), normal, inPlaneX);
  normalize(inPlaneX);
  let inPlaneY: Vector3 = [0, 0, 0];
  cross(normal, inPlaneX, inPlaneY);
  normalize(inPlaneY);

  if (rotationDeg) {
    const a = radiansFromDegrees(rotationDeg);
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    const rotatedX: Vector3 = [
      inPlaneX[0] * cos - inPlaneY[0] * sin,
      inPlaneX[1] * cos - inPlaneY[1] * sin,
      inPlaneX[2] * cos - inPlaneY[2] * sin,
    ];
    const rotatedY: Vector3 = [
      inPlaneX[0] * sin + inPlaneY[0] * cos,
      inPlaneX[1] * sin + inPlaneY[1] * cos,
      inPlaneX[2] * sin + inPlaneY[2] * cos,
    ];
    inPlaneX = rotatedX;
    inPlaneY = rotatedY;
  }

  // Semi-axes: project the drawn diagonal onto the in-plane axes.
  const diag: Vector3 = [cornerB[0] - cornerA[0], cornerB[1] - cornerA[1], cornerB[2] - cornerA[2]];
  const rx = Math.abs(dot(diag, inPlaneX)) / 2;
  const ry = Math.abs(dot(diag, inPlaneY)) / 2;

  // Through-plane voxel size along the normal, so exactly one slice is sampled.
  const origin = indexToWorld([0, 0, 0]);
  const projectStepOntoNormal = (idx: Vector3): number => {
    const step: Vector3 = [0, 0, 0];
    subtract(indexToWorld(idx), origin, step);
    return Math.abs(dot(step, normal));
  };
  const throughPlaneSpacing = Math.max(
    projectStepOntoNormal([1, 0, 0]),
    projectStepOntoNormal([0, 1, 0]),
    projectStepOntoNormal([0, 0, 1])
  );
  const halfThickness = throughPlaneSpacing / 2 + 1e-4;

  return { center, inPlaneX, inPlaneY, normal, rx, ry, halfThickness };
};

/** Orientation-independent ROI stencil: maps each voxel index to world via `indexToWorld`
 *  (must match the computeHistogram image) and tests it against the ellipse plane + radii. */
export const getWorldEllipseStencilFunction = (
  geometry: WorldEllipseGeometry,
  indexToWorld: (idx: Vector3) => Vector3
): StencilFunction => {
  const { center, inPlaneX, inPlaneY, normal, rx, ry, halfThickness } = geometry;
  // Degenerate ellipse (zero-width axis) encloses no area — match nothing rather than dividing by zero.
  if (!(rx > 0) || !(ry > 0)) return () => false;
  const rx2 = rx * rx;
  const ry2 = ry * ry;
  return (idx: Vector3): boolean => {
    const p = indexToWorld(idx);
    const dx = p[0] - center[0];
    const dy = p[1] - center[1];
    const dz = p[2] - center[2];

    // On the slice the ellipse was drawn on?
    const throughPlane = dx * normal[0] + dy * normal[1] + dz * normal[2];
    if (Math.abs(throughPlane) > halfThickness) return false;

    // Inside the ellipse (normalized distance < 1)?
    const du = dx * inPlaneX[0] + dy * inPlaneX[1] + dz * inPlaneX[2];
    const dv = dx * inPlaneY[0] + dy * inPlaneY[1] + dz * inPlaneY[2];
    return (du * du) / rx2 + (dv * dv) / ry2 < 1;
  };
};

export const rotateVector = (point: Vector3, angle: number, centroid: Vector3): Vector3 => {
  const sin = Math.sin(radiansFromDegrees(angle));
  const cos = Math.cos(radiansFromDegrees(angle));

  /**
   * A = vertex, point
   * B = triangle center, centroid
   * x' = cos * (A.x - B.x) + sin * (A.y - B.y) + B.x
   * y' = -sin * (A.x - B.x) + cos * (A.y - B.y) + B.y
   */
  const x = cos * (point[0] - centroid[0]) + sin * (point[1] - centroid[1]) + centroid[0];
  const y = -sin * (point[0] - centroid[0]) + cos * (point[1] - centroid[1]) + centroid[1];

  return [x, y, point[2]];
};

// Transform a vector from index-space to world-space
export function indexToWorldVec(
  transformer: {
    indexToWorld: (arg1: Vector3) => Vector3;
  },
  indexVector: Vector3
): Vector3 {
  // origin is always 0, 0, 0 in index space
  const indexOrigin: Vector3 = [0, 0, 0];
  const worldOrigin = transformer.indexToWorld(indexOrigin);
  const worldHead = transformer.indexToWorld(indexVector);
  const worldVec: Vector3 = [0, 0, 0];

  subtract(worldHead, worldOrigin, worldVec);
  return worldVec;
}

// Same as n % m but works for negative numbers too
export function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}
type TwoDPoint = [number, number];
type TwoDLine = [TwoDPoint, TwoDPoint];

export function check2DLineIntersection(
  [[line1StartX, line1StartY], [line1EndX, line1EndY]]: TwoDLine,
  [[line2StartX, line2StartY], [line2EndX, line2EndY]]: TwoDLine
): {
  x: number;
  y: number;
  onLine1: boolean;
  onLine2: boolean;
} {
  // if the lines intersect, the result contains the x and y of the intersection (treating the lines as infinite) and booleans for whether line segment 1 or line segment 2 contain the point
  let a = 0;
  let b = 0;
  let numerator1 = 0;
  let numerator2 = 0;
  const denominator =
    (line2EndY - line2StartY) * (line1EndX - line1StartX) -
    (line2EndX - line2StartX) * (line1EndY - line1StartY);
  const result = {
    x: -1,
    y: -1,
    onLine1: false,
    onLine2: false,
  };
  if (denominator === 0) {
    return result;
  }
  a = line1StartY - line2StartY;
  b = line1StartX - line2StartX;
  numerator1 = (line2EndX - line2StartX) * a - (line2EndY - line2StartY) * b;
  numerator2 = (line1EndX - line1StartX) * a - (line1EndY - line1StartY) * b;
  a = numerator1 / denominator;
  b = numerator2 / denominator;

  // if line1 is a segment and line2 is infinite, they intersect if:
  if (a > 0 && a < 1) {
    result.onLine1 = true;
  }
  // if line2 is a segment and line1 is infinite, they intersect if:
  if (b > 0 && b < 1) {
    result.onLine2 = true;
  }

  // if we cast these lines infinitely in both directions, they intersect here:
  result.x = line1StartX + a * (line1EndX - line1StartX);
  result.y = line1StartY + a * (line1EndY - line1StartY);

  // if line1 and line2 are segments, they intersect if both of the above are true
  return result;
}

export const rotatePointAroundPoint = (point: Vector3, pivot: Vector3, rads: number): Vector3 => {
  const [x0, y0] = pivot;
  const [x, y] = point;
  const cosA = Math.cos(rads);
  const sinA = Math.sin(rads);
  const dx = x - x0;
  const dy = y - y0;

  return [x0 + dx * cosA - dy * sinA, y0 + dx * sinA + dy * cosA, point[2]];
};

export const vec3ToVec2 = (vec3: Vector3): Vector2 => {
  return [vec3[0], vec3[1]];
};

/**
 * Using the Liang-Barsky algorithm, split the line segment by the bounding box.
 * Returning the points of intersection.
 */
function clipLineByBoundingBox(start: Vector3, end: Vector3, bbox: number[]): Segment | null {
  const [x0, y0, z0] = start;
  const [x1, y1, z1] = end;
  const [xmin, ymin, xmax, ymax] = bbox;
  const dx = x1 - x0;
  const dy = y1 - y0;
  let t0 = 0;
  let t1 = 1;
  let p, q, r;

  for (let edge = 0; edge < 4; edge++) {
    // Traverse through left, right, bottom, top edges.
    if (edge === 0) {
      p = -dx;
      q = -(xmin - x0);
    }
    if (edge === 1) {
      p = dx;
      q = xmax - x0;
    }
    if (edge === 2) {
      p = -dy;
      q = -(ymin - y0);
    }
    if (edge === 3) {
      p = dy;
      q = ymax - y0;
    }

    if (q == null || p == null) {
      return null;
    }

    r = q / p;

    if (p === 0 && q < 0) return null; // Don't draw line at all. (parallel line outside)

    if (p < 0) {
      if (r > t1)
        return null; // Don't draw line at all.
      else if (r > t0) t0 = r; // Line is clipped!
    } else if (p > 0) {
      if (r < t0)
        return null; // Don't draw line at all.
      else if (r < t1) t1 = r; // Line is clipped!
    }
  }

  return [
    [x0 + t0 * dx, y0 + t0 * dy, z0],
    [x0 + t1 * dx, y0 + t1 * dy, z1],
  ];
}

const findFirstIntersection = (
  segment: Segment,
  regions: Region[]
): [Segment, Region, number] | [null, null, null] => {
  for (const [idx, region] of regions.entries()) {
    const intersection = clipLineByBoundingBox(segment[0], segment[1], [...region.location]);

    if (intersection) {
      return [intersection, region, idx];
    }
  }

  return [null, null, null];
};

const sortPointsLToR = (segment: Segment): Segment => {
  const [point1, point2] = segment;

  if (point1[0] < point2[0]) {
    return [point1, point2];
  } else if (point1[0] > point2[0]) {
    return [point2, point1];
  } else {
    if (point1[1] < point2[1]) {
      return [point1, point2];
    } else if (point1[1] > point2[1]) {
      return [point2, point1];
    }

    return [point1, point2];
  }
};

const segmentLine = (fullLineSegments: Segment, subLineSegments: Segment): Segment[] => {
  const segments: Array<Segment> = [];
  const fullLine = sortPointsLToR(fullLineSegments);
  const subline = sortPointsLToR(subLineSegments);

  if (fullLine[0][0] < subline[0][0]) {
    segments.push([fullLine[0], subline[0]]);
  } else if (fullLine[0][1] < subline[0][1]) {
    segments.push([fullLine[0], subline[0]]);
  }

  if (fullLine[1][0] > subline[1][0]) {
    segments.push([subline[1], fullLine[1]]);
  } else if (fullLine[1][1] > subline[1][1]) {
    segments.push([subline[1], fullLine[1]]);
  }

  return segments;
};

/**
 * Two dimensional calculator of calibrated length using multiple regions.
 *
 * Given one line (two points), and list of regions, we can calculate the distance
 * across the regions.
 *
 * If the regions are all the same unit and there is no uncalibrated segment, we can return
 * a singular distance.
 *
 * If the regions have mixed units (s and m/s for example), then we can not combine them into a singular
 * distance value but return the two lines that would make up the edges of the triangle (as distance is the
 * hypotenuse).
 *
 * If we cross any uncalibrated space then the value is indeterminate without correction and we can only
 * return the distance across with pixels.
 *
 * The return value will be either:
 *   a singular value in calibrated units
 *   a singular value in pixels
 *   two line segments that contribute to the requested distance in mixed units
 */
export const getDistanceWithRegions = (
  segments: [Segment],
  regions: Region[],
  isMultiUnitSupported: boolean = false
): {
  distance: number;
  unit: string;
  subsegment?: Segment;
}[] => {
  const priorityOrderedRegions = regions.sort((a, b) => {
    if (a.flags.highPriority === b.flags.highPriority) {
      return 0;
    } else if (a.flags.highPriority === true && b.flags.highPriority === false) {
      return -1;
    } else {
      return 1;
    }
  });
  const pxDist = Math.sqrt(distance2BetweenPoints(segments[0][0], segments[0][1]));
  const remainingSegments = [segments[0]];

  let determinate = true;
  const currentUnit: [string | null, string | null] = [null, null];
  let currentDistance = 0;
  const distances: Array<{
    distance: number;
    subsegment?: Segment;
    unit: string;
  }> = [];
  // Create a stack of segments as we process multiple regions, overlapping regions can still be calculated
  while (remainingSegments.length > 0) {
    const nextSegment = remainingSegments.pop();
    // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'Segment | undefined' is not assignable to parameter of type 'Segment'.
    const [intersection, region, idx] = findFirstIntersection(nextSegment, priorityOrderedRegions);

    // If we do not find an intersection or region, we are in uncalibrated space and
    // we can only display the pixel measurement value.
    if (intersection == null || region == null || nextSegment == null) {
      determinate = false;
      break;
    }

    if (idx != null) {
      // Region is counted, remove
      // This prevents an infinite loop where the intersection might be a single point
      priorityOrderedRegions.splice(idx as number, 1);
    }

    // Find and update the current measured units based on region,
    // if they end up differing as we resegment the line we fall into unmeasureable
    // space and can only measure pixels
    const { physicalUnits, physicalDelta } = region as Region;
    if (
      (currentUnit[0] != null && currentUnit[0] !== physicalUnits[0]) ||
      (currentUnit[1] != null && currentUnit[1] !== physicalUnits[1])
    ) {
      determinate = false;
      break;
    }

    // Grab and set the physical units if not already set for both directions
    if (currentUnit[0] == null) {
      currentUnit[0] = physicalUnits[0];
    }

    if (currentUnit[1] == null) {
      currentUnit[1] = physicalUnits[1];
    }

    // TODO(VX-702): based on the spec, this might not be as described, it might still only use 1 single regions
    // dimensions. As we are currently only targeting tissue this is a lower incident usage.
    // If we have the same units across regions, we can sum and calculate distance
    if (currentUnit[0] === currentUnit[1]) {
      const scaled = intersection.map((point) => {
        const [x, y, z] = point;

        return [x * physicalDelta[0], y * physicalDelta[1], z];
      });

      currentDistance += Math.sqrt(
        distance2BetweenPoints(scaled[0] as Vector3, scaled[1] as Vector3)
      );
    } else if (currentUnit[0] != null && currentUnit[1] != null) {
      // If our units differ in direction we can not calculate one single distance and line
      // but need to display a vertical and horizontal line. Useful for seconds vs unit/second.
      const unitX = currentUnit[0];
      const unitY = currentUnit[1];
      const scaledY = [
        [intersection[0][0], intersection[0][1], 0],
        [intersection[0][0], intersection[1][1], 0],
      ].map((line) => {
        const [x, y, z] = line;

        return [x * physicalDelta[0], y * physicalDelta[1], z];
      }) as [Vector3, Vector3];
      const scaledX = [
        [intersection[0][0], intersection[1][1], 0],
        [intersection[1][0], intersection[1][1], 0],
      ].map((line) => {
        const [x, y, z] = line;

        return [x * physicalDelta[0], y * physicalDelta[1], z];
      }) as [Vector3, Vector3];

      distances.push({
        distance: Math.sqrt(distance2BetweenPoints(scaledY[0], scaledY[1])),
        unit: unitY,
        subsegment: [
          [intersection[0][0], intersection[0][1], 0],
          [intersection[0][0], intersection[1][1], 0],
        ],
      });
      distances.push({
        distance: Math.sqrt(distance2BetweenPoints(scaledX[0], scaledX[1])),
        unit: unitX,
        subsegment: [
          [intersection[0][0], intersection[1][1], 0],
          [intersection[1][0], intersection[1][1], 0],
        ],
      });
    }

    remainingSegments.push(...segmentLine(nextSegment, intersection));
  }

  // If we have determined we can not calculate distance, return pixels
  if (!determinate || currentUnit[0] == null || (distances.length === 2 && !isMultiUnitSupported)) {
    return [{ distance: pxDist, unit: 'px' }];
  }

  // Support mixed units with two lines
  if (distances.length === 2) {
    //we have subsegments;
    return distances;
  }

  // Our annotations only currently handle implicit `mm` within schema
  // If we have cm - convert it to mm.
  if (currentUnit[0] === 'cm') {
    return [{ distance: currentDistance * 10, unit: 'mm' }];
  }

  return [{ distance: currentDistance, unit: currentUnit[0] }];
};

export type Bounds = [number, number, number, number, number, number];

export const getBounds = (segments: Vector3[]): Bounds => {
  // Calculate world-bounds to scan for eligible voxels.
  // This will reduce the number of voxels that are fetched to calculate the stats.
  const scanBounds: Bounds = [
    Number.POSITIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
  ];

  // The bounds are stored as six-valued array as follows: [x_min, x_max, y_min, y_max, z_min, z_max].
  // The following loop ensures the correct ordering of values: x_min < x_max, y_min < y_max, z_min < z_max
  segments.forEach(([x, y, z]: [any, any, any]) => {
    if (x <= scanBounds[0]) {
      scanBounds[0] = x;
    }
    if (x >= scanBounds[1]) {
      scanBounds[1] = x;
    }

    if (y <= scanBounds[2]) {
      scanBounds[2] = y;
    }
    if (y >= scanBounds[3]) {
      scanBounds[3] = y;
    }

    if (z <= scanBounds[4]) {
      scanBounds[4] = z;
    }
    if (z >= scanBounds[5]) {
      scanBounds[5] = z;
    }
  });

  return scanBounds;
};

export type TransformMatrix = NonNullable<ImageRegistration['transformMatrix']>;

export function applyTransformMatrix(position: Vector3, transform: TransformMatrix): Vector3 {
  // Convert to 4d for transform. The extra 1 allows for more translational movement
  const positionVec = [...position, 1];

  const resultVec = [
    transform[0][0] * positionVec[0] +
      transform[0][1] * positionVec[1] +
      transform[0][2] * positionVec[2] +
      transform[0][3] * positionVec[3],
    transform[1][0] * positionVec[0] +
      transform[1][1] * positionVec[1] +
      transform[1][2] * positionVec[2] +
      transform[1][3] * positionVec[3],
    transform[2][0] * positionVec[0] +
      transform[2][1] * positionVec[1] +
      transform[2][2] * positionVec[2] +
      transform[2][3] * positionVec[3],
    transform[3][0] * positionVec[0] +
      transform[3][1] * positionVec[1] +
      transform[3][2] * positionVec[2] +
      transform[3][3] * positionVec[3],
  ];

  // remove fourth dimension to return to 3d
  // resultVec[3] will always be 1 and has no "information" in it
  return [resultVec[0], resultVec[1], resultVec[2]];
}

/**
 * [ux vx wx tx]
 * [uy vy wy ty]
 * [uz vz wz tz]
 * [ 0  0  0  1]
 * inverts to
 * [ux uy uz -ux*tx-uy*ty-uz*tz]
 * [vx vy vz -vx*tx-vy*ty-vz*tz]
 * [wx wy wz -wx*tx-wy*ty-wz*tz]
 * [ 0  0  0          1        ]
 */
export function invertTransformMatrix(t: TransformMatrix): TransformMatrix {
  return [
    [t[0][0], t[1][0], t[2][0], -t[0][0] * t[0][3] - t[1][0] * t[1][3] - t[2][0] * t[2][3]],
    [t[0][1], t[1][1], t[2][1], -t[0][1] * t[0][3] - t[1][1] * t[1][3] - t[2][1] * t[2][3]],
    [t[0][2], t[1][2], t[2][2], -t[0][2] * t[0][3] - t[1][2] * t[1][3] - t[2][2] * t[2][3]],
    [0, 0, 0, 1],
  ];
}

export type RegionOfInterestStats = {
  minimum: number;
  maximum: number;
  mean: number;
  stdDev: number;
  count: number;
  variance?: number;
};

export const DEFAULT_ROI: IComputeHistogram = {
  minimum: 0,
  maximum: 0,
  sigma: 0,
  average: 0,
  count: 0,
  variance: 0,
} as const;

/**
 * Utilizing the ROI stats we scale the known elements by the calculated SUV Scale Factor
 *
 * Returns the scaled stats
 */
export const getSUVROI = (
  stencilFunction: StencilFunction,
  bounds: Bounds,
  image: Nullable<vtkImageData>,
  suvScaleFactor: number
): RegionOfInterestStats => {
  const roi = getROI(stencilFunction, bounds, image);

  roi.minimum = roi.minimum * suvScaleFactor;
  roi.maximum = roi.maximum * suvScaleFactor;
  roi.mean = roi.mean * suvScaleFactor;
  roi.stdDev = roi.stdDev * Math.abs(suvScaleFactor);

  return roi;
};

/**
 * For given bounds, and a stencil function, calulates stats for the the image.
 *
 * Returns stats for the pixels within the bounds in the image
 *
 * This will be preferred most of the time only on PET with the calculatable scale factor will it
 * use the SUV ROI util.
 */
export const getROI = (
  stencilFunction: StencilFunction,
  bounds: Bounds,
  image?: vtkImageData | null
): RegionOfInterestStats => {
  const hasScalarData = !!(image && image.getPointData().getScalars());
  const histogramData = hasScalarData
    ? // Compute required metrics using the filter function
      (image.computeHistogram(bounds, stencilFunction) as IComputeHistogram)
    : DEFAULT_ROI;
  const { average, sigma, count, minimum, maximum, variance } = histogramData;

  // Zero-voxel match: vtk's computeHistogram leaves min/max at its ±Infinity seeds.
  // Surface as NaN (UI renders "—") instead of leaking Infinity or a misleading 0.
  const hasPixels = count > 0;
  return {
    count,
    variance,
    minimum: hasPixels ? minimum : NaN,
    maximum: hasPixels ? maximum : NaN,
    mean: hasPixels ? Math.round(average) : NaN,
    stdDev: hasPixels ? Math.round(sigma) : NaN,
  };
};

/**
 * Utility to determine if we can calculate the SUV scale factor given the tags for PET image

 * @returns {Boolean} whether we can calculate the SUV factor
 */
export function canCalculateScaleFactor(tags?: ScaleFactorTags | null): boolean {
  if (tags == null) {
    return false;
  }
  const petTags = tags.petImage;
  return (
    petTags?.preCalculatedSuvBwFactor != null ||
    (petTags != null &&
      CORRECTED_IMAGE_TAGS.every((tag) => petTags.correctedImage.includes(tag)) &&
      petTags.decayCorrection === 'START' &&
      // BQML scale factor is proportional to patient weight; a missing/zero weight would
      // silently produce a factor of 0 (and a 0,0 color scale) instead of surfacing as incalculable.
      (petTags.units !== PET_UNITS.BQML || (tags.patientStudy?.patientWeight ?? 0) > 0))
  );
}

/**
 * Given tags and study parameters calculate the Body weight version of the
 * scale factor for SUV with PET details
 *
 * @returns {number} The factor to scale the PET pixel values by
 */
export function calculateBwScaleFactor(tags?: ScaleFactorTags | null): number {
  if (tags == null) {
    return NaN;
  }
  const petTags = tags.petImage;
  const patientStudy = tags.patientStudy;

  if (petTags == null) {
    throw new Error('Can not calculate scale factor with no tags module');
  }

  if (petTags.preCalculatedSuvBwFactor != null) {
    return petTags.preCalculatedSuvBwFactor;
  }

  if (petTags.units === PET_UNITS.BQML) {
    const decayTime =
      (new Date(petTags.scanDateTime).valueOf() - new Date(petTags.startDateTime).valueOf()) / 1000;
    const injectedDose = petTags.radiopharmaceuticalInformation.radionuclideTotalDose ?? 0;
    const decayDose =
      injectedDose *
      Math.pow(2, -decayTime / (petTags.radiopharmaceuticalInformation.radionuclideHalfLife ?? 1));
    const weight = patientStudy?.patientWeight ?? 0;
    return (weight * 1000) / decayDose;
  } else if (petTags.units === PET_UNITS.CNTS && petTags.suvScaleFactor != null) {
    return petTags.suvScaleFactor; // Phillips private
  } else if (petTags.units === PET_UNITS.GML) {
    return 1;
  }

  throw new Error(`Unknown units: ${petTags.units}`);
}

/**
 * Given pixel values, and the study with tags for PET image, map the pixel values
 * to their SUV values for each pixel.
 *
 * From here, we can use the result to calulcate max, min, mean, etc.
 *
 * It can work on an array of numbers (similar to compute histogram) or a single pixel value
 *
 * No rounding is made on the numbers, any display format will be handled elsewhere
 *
 */
export function calculateBwSUV(pixelValues: number[], tags: Nullable<ScaleFactorTags>): number[];
export function calculateBwSUV(pixelValues: number, tags: Nullable<ScaleFactorTags>): number;

export function calculateBwSUV(pixelValues: number[] | number, tags: Nullable<ScaleFactorTags>) {
  if (tags == null || !canCalculateScaleFactor(tags)) {
    return null;
  }
  if (Array.isArray(pixelValues)) {
    const factor = calculateBwScaleFactor(tags);
    return pixelValues.map((v) => v * factor);
  } else {
    return pixelValues * calculateBwScaleFactor(tags);
  }
}

export const getOppositeCorners = (
  topLeft: Vector3,
  bottomRight: Vector3,
  {
    rotation,
    pivot,
  }: {
    pivot: Vector3;
    rotation: number;
  }
): [Vector3, Vector3] => {
  // these calculate the corners for the bounds of the ellipse
  // for gsps, we dont need to rotate our corners because we rotate the entire rectangle afterwards
  // for non-gsps, the user has anchored the top left and bottom right points of the ellipse with their mouse
  // and this calculate the corresponding corners with respect to that positon and the rotation value
  const topLeftRotated = rotatePointAroundPoint(topLeft, pivot, -2 * rotation); // top left rotated point
  const bottomRightRotated = rotatePointAroundPoint(bottomRight, pivot, -2 * rotation); // bottom right rotated point

  // Create an X-Y bounding box around the rotated points
  return [
    [bottomRightRotated[0], topLeftRotated[1], topLeft[2]],
    [topLeftRotated[0], bottomRightRotated[1], bottomRight[2]],
  ];
};

function isLeft(p: Vector3, x: Vector3, y: Vector3): number {
  return (y[0] - x[0]) * (p[1] - x[1]) - (p[0] - x[0]) * (y[1] - x[1]);
}

export const isPointInPolygon = (p: Nullable<Vector3>, lines: Segment[]): boolean => {
  let windingNumber = 0;

  if (p == null) {
    return false;
  }

  lines.forEach((line, i) => {
    const [a, b] = line;
    if (a[1] <= p[1]) {
      if (b[1] > p[1] && isLeft(p, a, b) > 0) {
        windingNumber += 1;
      }
    } else if (b[1] <= p[1] && isLeft(p, a, b) < 0) {
      windingNumber -= 1;
    }
  });

  return windingNumber !== 0;
};

export const calculateViewUpFromDirection = (direction: Matrix3x3): Vector3 => {
  const viewUp = [...IMAGE_VIEW_UPS['TWO_D_DRE']] as Vector3;

  const d3x3 = make3x3MatrixFromDirection(direction);

  multiply3x3_vect3(d3x3, viewUp, viewUp);
  return viewUp;
};

export const getMinMaxRepresentableValueByDataType = (dtype: string): [number, number] => {
  let min, max;
  switch (dtype) {
    case TypedArrayType.Uint8Array:
    case TypedArrayType.Uint8ClampedArray:
      min = 0;
      max = 255;
      break;

    case TypedArrayType.Int8Array:
      min = -128;
      max = 127;
      break;

    case TypedArrayType.Uint16Array:
      min = 0;
      max = 65535;
      break;

    case TypedArrayType.Int16Array:
      min = -32768;
      max = 32767;
      break;

    case TypedArrayType.Uint32Array:
      min = 0;
      max = 4294967295;
      break;

    case TypedArrayType.Int32Array:
      min = -2147483648;
      max = 2147483647;
      break;

    case TypedArrayType.Float32Array:
    case TypedArrayType.Float64Array:
    default:
      // UI-friendly clamp, adjust if needed
      min = -1e6;
      max = 1e6;
      break;
  }
  return [min, max];
};
